"use client";

import { useState, useEffect, useRef, type MouseEvent } from "react";
import type { StaticImageData } from "next/image";
import { FaRegFolder } from "react-icons/fa";
import { MdKeyboardArrowDown } from "react-icons/md";
import categoryAndGroupAPI from "../../services/categoryAndGroup/categoryAndGroupAPI";
import mainAPI from "../../services/main/mainAPI";
import sequenceAPI from "../../services/sequence/sequenceAPI";
import StatusPopUp from "./statusPopUp";
import ConfirmPopUp from "./confirmPopUp";
import {
    ThumnailFindTheMatch,
    ThumnailGameshowQuiz,
    ThumnailFlipCards,
    ThumnailAirPlanFlyingFruits,
    ThumnailFlappyBird,
    ThumnailWhackAMole,
    ThumnailAnagram,
    ThumnailCompleteTheSentence,
    ThumnailSituation,
    ThumnailSequence
} from "../../assets";
import { FiEdit3 } from "react-icons/fi";
import { IoClose } from "react-icons/io5";
import { useTranslations, useLocale } from "next-intl";
import {
    GAME_TYPE_TITLE_TO_API_KEY,
    getGameTypeLabel,
} from "../../utils/gameTypeLabels";
import {
    getMediaValidationErrorMessage,
    getMediaValidationTitle,
    validateImageFile,
} from "../games/shared/mediaValidation";

type CreatePopUpProps = {
    title: string;
    onClose: () => void;
    onConfirm: (createdGame: { id: number | string; exercise_name?: string; uuid?: string; description?: string }) => void;
};

type CreateFormField = "exerciseName" | "createBy" | "category" | "group" | "grade" | "thumbnail";

function getFileExtension(fileName: string) {
    const dotIndex = fileName.lastIndexOf(".");
    if (dotIndex < 0 || dotIndex === fileName.length - 1) {
        return "";
    }
    return fileName.slice(dotIndex);
}

function getFileExtensionFromUrl(pathOrUrl: string) {
    const cleanPath = String(pathOrUrl ?? "").split("?")[0];
    const fileName = cleanPath.split("/").pop() ?? "";
    return getFileExtension(fileName);
}

function getFileExtensionFromMimeType(mimeType: string) {
    const normalized = String(mimeType ?? "").trim().toLowerCase();
    if (!normalized) {
        return "";
    }

    const extensionByMime: Record<string, string> = {
        "image/png": ".png",
        "image/jpeg": ".jpg",
        "image/jpg": ".jpg",
        "image/webp": ".webp",
        "image/gif": ".gif",
        "image/bmp": ".bmp",
        "image/avif": ".avif",
        "image/svg+xml": ".svg",
        "image/x-icon": ".ico",
    };

    return extensionByMime[normalized] ?? "";
}

function getTeacherUuidFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return undefined;

        const parsed = JSON.parse(raw) as { id?: string };
        if (!parsed || typeof parsed !== "object") return undefined;
        if (!parsed.id || typeof parsed.id !== "string") return undefined;

        return parsed.id;
    } catch {
        return undefined;
    }
}

function getUserRoleFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const parsed = JSON.parse(raw) as { role?: string };
        if (!parsed || typeof parsed !== "object") return "";

        return typeof parsed.role === "string" ? parsed.role.trim() : "";
    } catch {
        return "";
    }
}

function getUserNameFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";

        const parsed = JSON.parse(raw) as { name?: string };
        if (!parsed || typeof parsed !== "object") return "";

        return typeof parsed.name === "string" ? parsed.name.trim() : "";
    } catch {
        return "";
    }
}

export default function CreateGamePopUp({ title, onClose, onConfirm }: CreatePopUpProps) {
    const [fileName, setFileName] = useState<string>("");
    const [exerciseName, setExerciseName] = useState<string>("");
    const [createBy, setCreateBy] = useState<string>(() => getUserNameFromLocalStorage());
    const [description, setDescription] = useState<string>("");
    const [selectedGrade, setSelectedGrade] = useState<string>("");
    const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
    const [categories, setCategories] = useState<Array<{ id: number; name: string }>>([]);
    const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);
    const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
    const [selectedGroupName, setSelectedGroupName] = useState<string>("");
    const [statusPopUp, setStatusPopUp] = useState<{
        title: string;
        message: string;
        type: "info" | "success" | "error";
    } | null>(null);
    const [createdGameData, setCreatedGameData] = useState<{ id: number | string; exercise_name?: string; uuid?: string; description?: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isConfirmCreateOpen, setIsConfirmCreateOpen] = useState(false);
    const [isThumbnailEditorOpen, setIsThumbnailEditorOpen] = useState(false);
    const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
    const [isPreparingDefaultThumbnail, setIsPreparingDefaultThumbnail] = useState(false);
    const [errorField, setErrorField] = useState<CreateFormField | null>(null);
    const [validationMessage, setValidationMessage] = useState<string | null>(null);
    const previewBlobUrlRef = useRef<string | null>(null);

    const tCG = useTranslations("createGame");
    const tMain = useTranslations("main");
    const locale = useLocale();

    const displayTitle = (() => {
        if (title === "Sequence") {
            return tMain("sequence");
        }
        const apiKey = GAME_TYPE_TITLE_TO_API_KEY[title];
        if (!apiKey) return title;
        return getGameTypeLabel(apiKey, (messageKey) => tMain(messageKey));
    })();

    const clearFieldError = (field: CreateFormField) => {
        setErrorField((current) => (current === field ? null : current));
    };

    const getInputBorderClass = (field: CreateFormField) =>
        errorField === field ? "border-red-500" : "border-[#bfc2c8]";

    const getLabelClass = (field: CreateFormField) =>
        errorField === field
            ? "text-xs font-medium text-red-500 sm:text-base"
            : "text-xs font-medium text-[#4E4E4E] sm:text-base";

    const getSelectTextClass = (value: string) =>
        value ? "text-[#4E4E4E]" : "text-gray-400";

    const gameTypeByTitle: Record<string, string> = {
        "Find the match": "find_the_match",
        "Game show Quiz": "game_show_quiz",
        "Flip cards": "flip_cards",
        "Flying Fruits": "air_plan/flying_fruits",
        "Flappy Chicken": "flappy_bird",
        "Whack a mole": "whack_a_mole",
        "Anagram": "anagram",
        "Complete the Sentence": "complete_the_sentence",
        "Situation": "situation",
    };

    const defaultThumbnailByGameType: Record<string, StaticImageData> = {
        find_the_match: ThumnailFindTheMatch,
        game_show_quiz: ThumnailGameshowQuiz,
        flip_cards: ThumnailFlipCards,
        "air_plan/flying_fruits": ThumnailAirPlanFlyingFruits,
        flappy_bird: ThumnailFlappyBird,
        whack_a_mole: ThumnailWhackAMole,
        anagram: ThumnailAnagram,
        complete_the_sentence: ThumnailCompleteTheSentence,
        situation: ThumnailSituation,
        sequence: ThumnailSequence,
    };

    const updateThumbnailPreview = (nextUrl: string, isBlobUrl: boolean) => {
        if (previewBlobUrlRef.current) {
            URL.revokeObjectURL(previewBlobUrlRef.current);
            previewBlobUrlRef.current = null;
        }

        if (isBlobUrl) {
            previewBlobUrlRef.current = nextUrl;
        }

        setThumbnailPreviewUrl(nextUrl);
    };

    const createFileFromDefaultThumbnail = async (thumbnailImage: StaticImageData, baseName: string) => {
        const response = await fetch(thumbnailImage.src);
        if (!response.ok) {
            return null;
        }

        const blob = await response.blob();
        const extension =
            getFileExtensionFromUrl(thumbnailImage.src) ||
            getFileExtensionFromMimeType(blob.type) ||
            ".png";

        return new File([blob], `${baseName}${extension}`, {
            type: blob.type || "image/png",
        });
    };

    const processAndCropThumbnail = async (file: File): Promise<File | null> => {
        const targetW = 429;
        const targetH = 239;

        const objectUrl = URL.createObjectURL(file);

        return await new Promise<File | null>((resolve) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const iw = img.naturalWidth;
                    const ih = img.naturalHeight;

                    let scale: number;
                    if (iw <= ih) {
                        scale = targetW / iw;
                    } else {
                        scale = targetH / ih;
                    }

                    const sw = Math.round(iw * scale);
                    const sh = Math.round(ih * scale);

                    const canvas = document.createElement("canvas");
                    canvas.width = targetW;
                    canvas.height = targetH;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        URL.revokeObjectURL(objectUrl);
                        resolve(null);
                        return;
                    }

                    const dx = Math.round((targetW - sw) / 2);
                    const dy = Math.round((targetH - sh) / 2);

                    ctx.clearRect(0, 0, targetW, targetH);
                    ctx.drawImage(img, dx, dy, sw, sh);

                    canvas.toBlob((blob) => {
                        URL.revokeObjectURL(objectUrl);
                        if (!blob) {
                            resolve(null);
                            return;
                        }

                        const extension = getFileExtensionFromMimeType(blob.type) || getFileExtension(file.name) || ".png";
                        const baseName = file.name.replace(/\.[^/.]+$/, "");
                        const newFile = new File([blob], `${baseName}${extension}`, { type: blob.type || "image/png" });
                        resolve(newFile);
                    }, "image/png");
                } catch (e) {
                    URL.revokeObjectURL(objectUrl);
                    resolve(null);
                }
            };
            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                resolve(null);
            };
            img.src = objectUrl;
        });
    };

    useEffect(() => {
        let mounted = true;
        const teacherUuid = getTeacherUuidFromLocalStorage();

        categoryAndGroupAPI
            .getCategoriesByTeacherUuid(teacherUuid)
            .then((res: any) => {
                const data = Array.isArray(res) ? res : res?.data ?? [];
                if (mounted) setCategories(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) setCategories([]);
            });

        categoryAndGroupAPI
            .getGroupsByTeacherUuid(teacherUuid)
            .then((res: any) => {
                const data = Array.isArray(res) ? res : res?.data ?? [];
                if (mounted) setGroups(Array.isArray(data) ? data : []);
            })
            .catch(() => {
                if (mounted) setGroups([]);
            });

        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        let active = true;

        const loadDefaultThumbnail = async () => {
            const resolvedGameType = title === "Sequence" ? "sequence" : gameTypeByTitle[title];
            const defaultThumbnail = resolvedGameType ? defaultThumbnailByGameType[resolvedGameType] : null;

            setIsThumbnailEditorOpen(false);
            setFileName("");
            setThumbnailFile(null);

            if (!defaultThumbnail || !resolvedGameType) {
                updateThumbnailPreview("", false);
                return;
            }

            setIsPreparingDefaultThumbnail(true);
            updateThumbnailPreview(defaultThumbnail.src, false);

            const defaultFile = await createFileFromDefaultThumbnail(
                defaultThumbnail,
                `thumbnail_default_${resolvedGameType.replace(/[\/]/g, "_")}`
            );

            if (!active) {
                return;
            }

            if (!defaultFile) {
                setStatusPopUp({
                    title: tCG("fail"),
                    message: tCG("thumbnailError"),
                    type: "error",
                });
                setIsPreparingDefaultThumbnail(false);
                return;
            }

            setThumbnailFile(defaultFile);
            setFileName(defaultFile.name);
            setIsPreparingDefaultThumbnail(false);
            clearFieldError("thumbnail");
        };

        void loadDefaultThumbnail();

        return () => {
            active = false;
        };
    }, [title]);

    useEffect(() => {
        return () => {
            if (previewBlobUrlRef.current) {
                URL.revokeObjectURL(previewBlobUrlRef.current);
                previewBlobUrlRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const scrollY = window.scrollY;
        const previousBodyOverflow = document.body.style.overflow;
        const previousBodyPosition = document.body.style.position;
        const previousBodyTop = document.body.style.top;
        const previousBodyWidth = document.body.style.width;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.documentElement.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousBodyOverflow;
            document.body.style.position = previousBodyPosition;
            document.body.style.top = previousBodyTop;
            document.body.style.width = previousBodyWidth;
            document.documentElement.style.overflow = previousHtmlOverflow;
            window.scrollTo(0, scrollY);
        };
    }, []);

    const showValidationError = (fieldLabel: string) => {
        setStatusPopUp({
            title: tCG("alert"),
            message: `${tCG("please")} ${fieldLabel}`,
            type: "info",
        });
    };

    const validateCreateForm = () => {
        if (isPreparingDefaultThumbnail) {
            setErrorField("thumbnail");
            setStatusPopUp({
                title: tCG("alert"),
                message: tCG("preparingThumbnail"),
                type: "info",
            });
            return false;
        }

        if (!exerciseName.trim()) {
            setErrorField("exerciseName");
            showValidationError(tCG("enterGameName"));
            return false;
        }

        if (!createBy.trim()) {
            setErrorField("createBy");
            showValidationError(tCG("enterCreatorName"));
            return false;
        }

        if (!selectedCategoryName) {
            setErrorField("category");
            showValidationError(tCG("selectCategory"));
            return false;
        }

        if (!selectedGroupName) {
            setErrorField("group");
            showValidationError(tCG("selectGroup"));
            return false;
        }

        if (!selectedGrade) {
            setErrorField("grade");
            showValidationError(tCG("selectGrade"));
            return false;
        }

        if (!thumbnailFile) {
            setErrorField("thumbnail");
            showValidationError(tCG("attachImageFile"));
            return false;
        }

        setErrorField(null);
        return true;
    };

    const handleSubmitCreateGame = async () => {
        if (!validateCreateForm()) {
            return;
        }

        const teacherUuid = getTeacherUuidFromLocalStorage();
        const isSuperAdmin = getUserRoleFromLocalStorage() === "super_admin";

        try {
            setIsSubmitting(true);

            const isSequence = title === "Sequence";

            if (!isSequence) {
                const gameType = gameTypeByTitle[title];
                if (!gameType) {
                    setStatusPopUp({
                        title: tCG("fail"),
                        message: tCG("templateGameTypeNotFound"),
                        type: "error",
                    });
                    setIsSubmitting(false);
                    return;
                }

                const thumbnailExtension = getFileExtension(thumbnailFile.name);
                const thumbnailUploadFileName = `thumbnail_${Date.now()}${thumbnailExtension}`;

                const response = await mainAPI.createGame({
                    create_by: createBy.trim(),
                    exercise_name: exerciseName.trim(),
                    description: description.trim(),
                    subject: selectedCategoryName,
                    group_id: groups.find((g) => g.name === selectedGroupName)?.id,
                    teacher_uuid: teacherUuid,
                    question_type: "single_question",
                    game_type: gameType,
                    thumbnail: thumbnailFile,
                    thumbnailFileName: thumbnailUploadFileName,
                    game_default: isSuperAdmin,
                    class: selectedGrade,
                });

                if (response?.status === "failed") {
                    setStatusPopUp({
                        title: tCG("fail"),
                        message: tCG("createGameFail"),
                        type: "error",
                    });
                    return;
                }

                setStatusPopUp({
                    title: tCG("success"),
                    message: tCG("createGameSuccess"),
                    type: "success",
                });
                setCreatedGameData({
                    id: response?.id,
                    exercise_name: response?.exercise_name,
                    uuid: response?.uuid,
                    description: description.trim(),
                });
            } else {
                const thumbnailExtension = getFileExtension(thumbnailFile.name);
                const thumbnailUploadFileName = `thumbnail_${Date.now()}${thumbnailExtension}`;

                const response = await sequenceAPI.createSequence({
                    create_by: createBy.trim(),
                    exercise_name: exerciseName.trim(),
                    description: description.trim(),
                    subject: selectedCategoryName,
                    group_id: groups.find((g) => g.name === selectedGroupName)?.id,
                    teacher_uuid: teacherUuid,
                    thumbnail: thumbnailFile,
                    thumbnailFileName: thumbnailUploadFileName,
                    sequence_default: isSuperAdmin,
                    class: selectedGrade,
                });

                if (response?.status === "failed") {
                    setStatusPopUp({
                        title: tCG("fail"),
                        message: tCG("createSequenceFail"),
                        type: "error",
                    });
                    return;
                }

                setStatusPopUp({
                    title: tCG("success"),
                    message: tCG("createSequenceSuccess"),
                    type: "success",
                });
                setCreatedGameData({
                    id: response?.id,
                    exercise_name: response?.exercise_name,
                    uuid: response?.uuid,
                    description: description.trim(),
                });
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseStatus = () => {
        const isSuccess = statusPopUp?.type === "success";
        const gameData = createdGameData;
        setStatusPopUp(null);

        if (isSuccess && gameData?.id) {
            onConfirm(gameData);
        }
    };

    const handleClickCreateButton = () => {
        if (isSubmitting) {
            return;
        }

        if (!validateCreateForm()) {
            return;
        }

        setIsConfirmCreateOpen(true);
    };

    const handleCancelConfirmCreate = () => {
        setIsConfirmCreateOpen(false);
    };

    const handleConfirmCreate = () => {
        setIsConfirmCreateOpen(false);
        void handleSubmitCreateGame();
    };

    const handleRequestClose = () => {
        if (isSubmitting || isConfirmCreateOpen || statusPopUp) {
            return;
        }

        onClose();
    };

    const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
        if (event.target !== event.currentTarget) {
            return;
        }

        handleRequestClose();
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden overscroll-none bg-black/45 px-3 py-2 sm:p-4"
            onClick={handleBackdropClick}
        >
            <div
                className="relative flex w-full max-w-[400px] max-h-[calc(100dvh-1rem)] flex-col rounded-md bg-[#F2F2F2] shadow-[0_6px_20px_rgba(0,0,0,0.35)] sm:max-h-[95vh] sm:max-w-150 sm:overflow-hidden"
                onClick={(event) => event.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={handleRequestClose}
                    disabled={isSubmitting}
                    className="absolute right-1.5 top-1.5 z-10 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full bg-[#d5d9dd] text-[#8e949b] hover:bg-[#d5d9dd]/90 disabled:cursor-not-allowed disabled:opacity-50 sm:right-3 sm:top-3 sm:h-9 sm:w-9"
                    aria-label="ปิด"
                >
                    <IoClose className="text-base sm:text-xl" />
                </button>

                <div className="px-2.5 pb-2 pt-2.5 sm:min-h-0 sm:flex-1 sm:overflow-y-auto sm:overscroll-contain sm:p-5 sm:pb-12 sm:pt-8">
                <div className="mx-auto w-full max-w-[320px] rounded-sm border border-[#d6c844] bg-[#f2e44b] py-0.5 text-center text-xs font-semibold leading-tight text-[#2B2B2B] shadow-[4px_4px_0_#5d6670] sm:max-w-[380px] sm:py-1 sm:text-xl sm:leading-normal">
                    {displayTitle}
                </div>

                <form className="mt-2 space-y-1 text-[10px] text-[#404040] sm:mt-4 sm:space-y-3">
                    <label className="block">
                        <span className={getLabelClass("exerciseName")}>{tCG("gameName")} <span className="text-red-500">*</span></span>
                        <input
                            type="text"
                            value={exerciseName}
                            placeholder={tCG("pleaseEnterGameName")}
                            onChange={(event) => {
                                setExerciseName(event.target.value);
                                clearFieldError("exerciseName");
                            }}
                            className={`mt-0.5 h-7 w-full rounded-lg border ${getInputBorderClass("exerciseName")} bg-white px-2 text-xs outline-none cursor-pointer sm:mt-1 sm:h-9 sm:text-base`}
                        />
                    </label>

                    <label className="block">
                        <span className={getLabelClass("createBy")}>{tCG("createdBy")} <span className="text-red-500">*</span></span>
                        <input
                            type="text"
                            value={createBy}
                            onChange={(event) => {
                                setCreateBy(event.target.value);
                                clearFieldError("createBy");
                            }}
                            className={`mt-0.5 h-7 w-full rounded-lg border ${getInputBorderClass("createBy")} bg-white px-2 text-xs outline-none cursor-pointer sm:mt-1 sm:h-9 sm:text-base`}
                        />
                    </label>

                    <label className="block">
                        <span className="text-xs font-medium text-[#4E4E4E] sm:text-base">{tCG("details")}</span>
                        <input
                            type="text"
                            value={description}
                            placeholder={tCG("pleaseEnterDetails")}
                            onChange={(event) => setDescription(event.target.value)}
                            className="mt-0.5 h-7 w-full rounded-lg border border-[#bfc2c8] bg-white px-2 text-xs outline-none cursor-pointer sm:mt-1 sm:h-9 sm:text-base"
                        />
                    </label>

                    <label className="block">
                        <span className={getLabelClass("category")}>{tCG("category")} <span className="text-red-500">*</span></span>
                        <div className="mt-0.5 relative sm:mt-1">
                            <select
                                value={selectedCategoryName}
                                onChange={(event) => {
                                    setSelectedCategoryName(event.target.value);
                                    clearFieldError("category");
                                }}
                                className={`h-7 w-full rounded-lg border ${getInputBorderClass("category")} ${getSelectTextClass(selectedCategoryName)} bg-white px-2 pr-7 text-xs outline-none cursor-pointer appearance-none sm:h-9 sm:pr-9 sm:text-base`}
                            >
                                <option value="" className="text-gray-400">{tCG("pleaseSelectCategory")}</option>
                                {categories.map((c) => (
                                    <option key={c.id} value={c.name} className="text-[#4E4E4E]">
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                <MdKeyboardArrowDown size={20} />
                            </span>
                        </div>
                    </label>

                    <label className="block">
                        <span className={getLabelClass("group")}>{tCG("group")} <span className="text-red-500">*</span></span>
                        <div className="mt-0.5 relative sm:mt-1">
                            <select
                                value={selectedGroupName}
                                onChange={(event) => {
                                    setSelectedGroupName(event.target.value);
                                    clearFieldError("group");
                                }}
                                className={`h-7 w-full rounded-lg border ${getInputBorderClass("group")} ${getSelectTextClass(selectedGroupName)} bg-white px-2 pr-7 text-xs outline-none cursor-pointer appearance-none sm:h-9 sm:pr-9 sm:text-base`}
                            >
                                <option value="" className="text-gray-400">{tCG("pleaseSelectGroup")}</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.name} className="text-[#4E4E4E]">
                                        {g.name}
                                    </option>
                                ))}
                            </select>
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                <MdKeyboardArrowDown size={20} />
                            </span>
                        </div>
                    </label>

                    <label className="block">
                        <span className={getLabelClass("grade")}>{tCG("grade")} <span className="text-red-500">*</span></span>
                        <div className="mt-0.5 relative sm:mt-1">
                            <select
                                value={selectedGrade}
                                onChange={(event) => {
                                    setSelectedGrade(event.target.value);
                                    clearFieldError("grade");
                                }}
                                className={`h-7 w-full rounded-lg border ${getInputBorderClass("grade")} ${getSelectTextClass(selectedGrade)} bg-white px-2 pr-7 text-xs outline-none cursor-pointer appearance-none sm:h-9 sm:pr-9 sm:text-base`}
                            >
                                <option value="" className="text-gray-400">{tCG("pleaseSelectGrade")}</option>
                                <option value="ป.1" className="text-[#4E4E4E]">{tCG("grade1")}</option>
                                <option value="ป.2" className="text-[#4E4E4E]">{tCG("grade2")}</option>
                                <option value="ป.3" className="text-[#4E4E4E]">{tCG("grade3")}</option>
                                <option value="ป.4" className="text-[#4E4E4E]">{tCG("grade4")}</option>
                                <option value="ป.5" className="text-[#4E4E4E]">{tCG("grade5")}</option>
                                <option value="ป.6" className="text-[#4E4E4E]">{tCG("grade6")}</option>
                                <option value="ม.1" className="text-[#4E4E4E]">{tCG("grade7")}</option>
                                <option value="ม.2" className="text-[#4E4E4E]">{tCG("grade8")}</option>
                                <option value="ม.3" className="text-[#4E4E4E]">{tCG("grade9")}</option>
                            </select>
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">
                                <MdKeyboardArrowDown size={20} />
                            </span>
                        </div>
                    </label>

                    <label className="block">
                        <span className={`${getLabelClass("thumbnail")} text-[10px] leading-snug sm:text-base sm:leading-normal`}>
                            {tCG("thumbnail")} <span className="text-red-500">*</span>
                        </span>
                        <div className="mt-1 flex justify-center sm:mt-2">
                            {thumbnailPreviewUrl ? (
                                <img
                                    src={thumbnailPreviewUrl}
                                    alt="thumbnail preview"
                                    width={429}
                                    height={239}
                                    className={`h-auto w-full max-w-full border-2 object-cover sm:max-w-[429px] ${errorField === "thumbnail" ? "border-red-500" : "border-transparent"}`}
                                />
                            ) : (
                                <div className={`flex aspect-[429/239] w-full max-w-full items-center justify-center rounded-sm border bg-[#f5f5f5] text-[10px] text-[#9b9b9b] sm:max-w-[429px] sm:text-base ${errorField === "thumbnail" ? "border-red-500" : "border-[#d9d9d9]"}`}>
                                    {tCG("noImage")}
                                </div>
                            )}
                        </div>

                        <button
                            type="button"
                            className="mt-1 flex w-full items-center justify-center text-center text-[11px] font-medium text-[#0092D7] sm:mt-3 sm:text-base"
                        >
                            <FiEdit3 className="mr-1 text-base sm:mr-2 sm:text-[22px]" />{" "}
                            <span className="cursor-pointer" onClick={() => setIsThumbnailEditorOpen((prev) => !prev)}>
                                {tCG("editThumbnail")}
                            </span>
                        </button>

                        {isThumbnailEditorOpen ? (
                            <div className="mt-1 relative sm:mt-2">
                                <input
                                    type="file"
                                    accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                    onChange={async (event) => {
                                        const file = event.target.files?.[0];
                                        if (!file) {
                                            setFileName("");
                                            setThumbnailFile(null);
                                            updateThumbnailPreview("", false);
                                            event.currentTarget.value = "";
                                            return;
                                        }

                                        if (!validateImageFile(file)) {
                                            setValidationMessage(getMediaValidationErrorMessage("image", locale));
                                            event.currentTarget.value = "";
                                            return;
                                        }

                                        setFileName(file.name);
                                        clearFieldError("thumbnail");

                                        const processed = await processAndCropThumbnail(file);
                                        const usedFile = processed ?? file;

                                        setThumbnailFile(usedFile);
                                        const previewUrl = URL.createObjectURL(usedFile);
                                        updateThumbnailPreview(previewUrl, true);
                                        event.currentTarget.value = "";
                                    }}
                                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                />
                                <div className={`flex h-7 w-full items-center justify-between rounded-lg border ${getInputBorderClass("thumbnail")} bg-white px-2 text-xs sm:h-9 sm:px-3 sm:text-base`}>
                                    <span className="truncate text-xs text-[#666] sm:text-sm">
                                        {fileName || ""}
                                    </span>
                                    <FaRegFolder className="text-xl text-[#666]" />
                                </div>
                            </div>
                        ) : null}
                    </label>

                    <div className="mt-2 sm:static sm:mt-0">
                        <div className="flex flex-row items-center justify-center gap-2.5 sm:gap-6 sm:pt-6">
                            <button
                                type="button"
                                onClick={handleClickCreateButton}
                                disabled={isSubmitting || isPreparingDefaultThumbnail}
                                className="flex min-w-12 flex-1 cursor-pointer items-center justify-center rounded-md bg-[#62AC4B] px-3 py-1 text-xs font-semibold text-white sm:min-w-16 sm:flex-none sm:px-7 sm:py-2 sm:text-base"
                            >
                                {isSubmitting ? tCG("saving") : isPreparingDefaultThumbnail ? tCG("ok") : tCG("ok")}
                            </button>
                            <button
                                type="button"
                                onClick={handleRequestClose}
                                disabled={isSubmitting}
                                className="flex min-w-12 flex-1 cursor-pointer items-center justify-center rounded-md bg-[#009aeb] px-3 py-1 text-xs font-semibold text-[#ffffff] disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-16 sm:flex-none sm:px-6 sm:py-2 sm:text-base"
                            >
                                {tCG("cancel")}
                            </button>
                        </div>
                    </div>
                </form>
                </div>
            </div>

            {isConfirmCreateOpen ? (
                <div onClick={(event) => event.stopPropagation()}>
                    <ConfirmPopUp
                        title={tCG("confirmCreateGame")}
                        message={tCG("confirmCreateGameMessage")}
                        cancelText={tCG("cancel")}
                        confirmText={tCG("confirm")}
                        onCancel={handleCancelConfirmCreate}
                        onConfirm={handleConfirmCreate}
                        isConfirming={isSubmitting}
                    />
                </div>
            ) : null}

            {statusPopUp ? (
                <div onClick={(event) => event.stopPropagation()}>
                    <StatusPopUp
                        title={statusPopUp.title}
                        message={statusPopUp.message}
                        type={statusPopUp.type}
                        onClose={handleCloseStatus}
                    />
                </div>
            ) : null}

            {validationMessage ? (
                <div onClick={(event) => event.stopPropagation()}>
                    <StatusPopUp
                        title={getMediaValidationTitle(locale)}
                        message={validationMessage}
                        type="error"
                        onClose={() => setValidationMessage(null)}
                    />
                </div>
            ) : null}
        </div>
    );
}
